import { getStorage } from '../utils/localOrSessionStorage';

/**
 * Holds requests that failed because the signed-in session expired, until the
 * person signs in again (in another tab, so the page they were on — and any
 * unsaved form state — stays intact). On a fresh token every held request is
 * replayed; on sign-out they are rejected with their original 401.
 */

type Waiter = { resolve: (token: string) => void; reject: () => void };

const TOKEN_KEY = 'jwtToken';

let expiredToken: string | null = null;
let waiters: Waiter[] = [];
const listeners = new Set<(expired: boolean) => void>();

const notify = () => {
  const expired = expiredToken !== null;
  listeners.forEach((listener) => listener(expired));
};

export const isSessionExpired = () => expiredToken !== null;

export const subscribeToSessionExpiry = (
  listener: (expired: boolean) => void
) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** The user id a JWT was issued to, read without verifying it (the API does that). */
const tokenSubject = (token: string): string | null => {
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const sub = JSON.parse(json).sub;
    return typeof sub === 'string' && sub ? sub : null;
  } catch {
    return null;
  }
};

/**
 * Resolves with a fresh token once the person has signed in again, or rejects
 * if they choose to sign out instead. `staleToken` is the token the failed
 * request carried: if a newer one is already stored, it resolves immediately.
 */
export const waitForReauthentication = (staleToken: string) => {
  const current = getStorage().getItem(TOKEN_KEY);
  if (current && current !== staleToken) {
    const sameUser =
      tokenSubject(current) !== null &&
      tokenSubject(current) === tokenSubject(staleToken);
    if (sameUser) {
      return Promise.resolve(current);
    }
    // Another account signed in elsewhere: this tab's state is the old
    // person's, and every later request would carry the new token.
    window.location.reload();
    return Promise.reject(new Error('Signed in as someone else'));
  }

  return new Promise<string>((resolve, reject) => {
    waiters.push({ resolve, reject });
    if (expiredToken === null) {
      expiredToken = staleToken;
      notify();
    }
  });
};

/**
 * Replays held requests if a token other than the expired one is now stored,
 * but only when it was issued to the same person: a request built for one
 * account must never run with another's privileges. A different account means
 * this tab's state belongs to someone else, so the held requests are dropped
 * and the tab reloads as whoever is signed in now.
 */
export const checkForNewToken = () => {
  if (expiredToken === null) {
    return false;
  }
  const current = getStorage().getItem(TOKEN_KEY);
  if (!current || current === expiredToken) {
    return false;
  }

  const expiredSubject = tokenSubject(expiredToken);
  if (!expiredSubject || tokenSubject(current) !== expiredSubject) {
    abandonHeldRequests();
    window.location.reload();
    return false;
  }

  const pending = waiters;
  waiters = [];
  expiredToken = null;
  pending.forEach((waiter) => waiter.resolve(current));
  notify();
  return true;
};

/** Gives up on the held requests (the person chose to sign out). */
export const abandonHeldRequests = () => {
  const pending = waiters;
  waiters = [];
  expiredToken = null;
  pending.forEach((waiter) => waiter.reject());
  notify();
};

if (typeof window !== 'undefined') {
  // Fires in this tab when another tab writes the token after signing in.
  window.addEventListener('storage', (event) => {
    if (event.key === TOKEN_KEY) {
      checkForNewToken();
    }
  });
}
